import { isSurveyLikertPointCount, resolveLikertLabels } from './likert-labels'
import type {
  SurveyDateInputFormat,
  SurveyChoiceTextResponse,
  SurveyImageSize,
  SurveyLikertPointCount,
  SurveyOption,
  SurveyQuestion,
  SurveyQuestionType,
} from './survey-question.types'

const VALID_TYPES = new Set<string>([
  'yes_no',
  'multiple_choice',
  'checkbox',
  'dropdown',
  'picture_choice',
  'ranking',
  'short_text',
  'long_text',
  'date',
  'rating',
  'likert',
  'slider',
  'video',
])

export function readString(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = obj[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return undefined
}

export function parseOption(row: unknown, index: number): SurveyOption | null {
  if (row == null || typeof row !== 'object') return null
  const o = row as Record<string, unknown>
  const id = readString(o, 'id', '_id') ?? `opt-${index}`
  const label = readString(o, 'label', 'text', 'value') ?? `Option ${index + 1}`
  const imageUrl =
    typeof o.imageUrl === 'string' && o.imageUrl.trim() ? o.imageUrl.trim() : null
  return { id, label, imageUrl }
}

export function resolveQuestionType(rawType: string): SurveyQuestionType {
  if (rawType === 'unsupported') return 'unsupported'
  return VALID_TYPES.has(rawType) ? (rawType as SurveyQuestionType) : 'unsupported'
}

export function parseLikertFields(q: Record<string, unknown>) {
  const rawCount = q.likertPointCount
  const likertPointCount: SurveyLikertPointCount =
    typeof rawCount === 'number' && isSurveyLikertPointCount(rawCount) ? rawCount : 5
  return {
    likertPointCount,
    likertLabels: resolveLikertLabels(likertPointCount, q.likertLabels),
    likertLayoutHorizontal: q.likertLayoutHorizontal === true,
  }
}

export function parseTextLengthFields(q: Record<string, unknown>) {
  const minLength =
    typeof q.minLength === 'number' && q.minLength >= 1 ? Math.floor(q.minLength) : null
  const maxLength =
    typeof q.maxLength === 'number' && q.maxLength >= 1 ? Math.floor(q.maxLength) : null
  return { minLength, maxLength }
}

export function parseDateFields(q: Record<string, unknown>) {
  const dateInputFormat: SurveyDateInputFormat =
    q.dateInputFormat === 'MM/dd/yyyy' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'
  const earliestSelectableDate =
    typeof q.earliestSelectableDate === 'string' && q.earliestSelectableDate.trim()
      ? q.earliestSelectableDate.trim().slice(0, 10)
      : null
  const latestSelectableDate =
    typeof q.latestSelectableDate === 'string' && q.latestSelectableDate.trim()
      ? q.latestSelectableDate.trim().slice(0, 10)
      : null
  return {
    dateInputFormat,
    earliestSelectableDate,
    latestSelectableDate,
    disablePastDates: q.disablePastDates === true,
    disableFutureDates: q.disableFutureDates === true,
  }
}

export function parseImageSize(q: Record<string, unknown>): SurveyImageSize {
  return q.imageSize === 'small' || q.imageSize === 'large' ? q.imageSize : 'medium'
}

function parseQuestionOptions(q: Record<string, unknown>) {
  if (!Array.isArray(q.options)) return []
  return q.options
    .map((o, i) => parseOption(o, i))
    .filter((o): o is NonNullable<typeof o> => o != null)
}

function parseRatingFields(q: Record<string, unknown>) {
  return {
    ratingScaleMax: q.ratingScaleMax === 10 ? 10 : 5,
    ratingDisplayStyle: q.ratingDisplayStyle === 'numbers' ? 'numbers' : 'stars',
    ratingLowLabel: readString(q, 'ratingLowLabel') ?? '',
    ratingHighLabel: readString(q, 'ratingHighLabel') ?? '',
  } as const
}

function parseSliderFields(q: Record<string, unknown>) {
  return {
    sliderMinValue: typeof q.sliderMinValue === 'number' ? q.sliderMinValue : 0,
    sliderMaxValue: typeof q.sliderMaxValue === 'number' ? q.sliderMaxValue : 100,
    sliderStep: typeof q.sliderStep === 'number' && q.sliderStep >= 1 ? q.sliderStep : 1,
    sliderLowLabel: readString(q, 'sliderLowLabel') ?? '',
    sliderHighLabel: readString(q, 'sliderHighLabel') ?? '',
  } as const
}

function parseSelectionFields(q: Record<string, unknown>) {
  return {
    minSelections: typeof q.minSelections === 'number' ? q.minSelections : null,
    maxSelections: typeof q.maxSelections === 'number' ? q.maxSelections : null,
    placeholder:
      typeof q.placeholder === 'string' && q.placeholder.trim()
        ? q.placeholder.trim()
        : null,
    randomizeOrder: q.randomizeOrder === true,
    layoutHorizontal: q.layoutHorizontal === true,
    multiColumn: q.multiColumn === true,
    yesNoToggleStyle: q.yesNoToggleStyle === true,
    hasOtherOption: q.hasOtherOption === true,
    hasNoneOfAboveOption: q.hasNoneOfAboveOption === true,
    ...parseTextResponseField(q),
  } as const
}

const TEXT_RESPONSE_TARGETS = new Set(['option', 'other', 'none'])

function readCharacterLimit(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 1 ? raw : null
}

/** BFP-1156 — omit the key when absent so legacy questions keep `textResponse === undefined`. */
function parseTextResponseField(
  q: Record<string, unknown>,
): { textResponse?: SurveyChoiceTextResponse | null } {
  if (!('textResponse' in q) || q.textResponse === undefined) return {}
  const raw = q.textResponse
  if (raw == null || typeof raw !== 'object') return { textResponse: null }
  const tr = raw as Record<string, unknown>
  if (typeof tr.target !== 'string' || !TEXT_RESPONSE_TARGETS.has(tr.target)) {
    return { textResponse: null }
  }
  const optionId = typeof tr.optionId === 'string' && tr.optionId ? tr.optionId : undefined
  if (tr.target === 'option' && !optionId) return { textResponse: null }
  return {
    textResponse: {
      target: tr.target as SurveyChoiceTextResponse['target'],
      ...(optionId ? { optionId } : {}),
      minCharacters: readCharacterLimit(tr.minCharacters),
      maxCharacters: readCharacterLimit(tr.maxCharacters),
    },
  }
}

export function parseSurveyQuestionRow(row: unknown, index: number): SurveyQuestion | null {
  if (row == null || typeof row !== 'object') return null
  const q = row as Record<string, unknown>
  const id = readString(q, 'id', '_id')
  if (!id) return null

  const type = resolveQuestionType(readString(q, 'type') ?? 'short_text')

  return {
    id,
    order: typeof q.order === 'number' ? q.order : index,
    type,
    prompt: readString(q, 'prompt', 'text') ?? 'Question',
    description:
      typeof q.description === 'string' && q.description.trim()
        ? q.description.trim()
        : null,
    mandatory: q.mandatory === true || q.required === true,
    options: parseQuestionOptions(q),
    yesLabel: readString(q, 'yesLabel') ?? 'Yes',
    noLabel: readString(q, 'noLabel') ?? 'No',
    ...parseSelectionFields(q),
    ...parseRatingFields(q),
    ...parseLikertFields(q),
    ...parseSliderFields(q),
    imageSize: parseImageSize(q),
    ...parseTextLengthFields(q),
    ...parseDateFields(q),
    videoUrl:
      typeof q.videoUrl === 'string' && q.videoUrl.trim() ? q.videoUrl.trim() : null,
  }
}
